import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CopyButton from '../../components/shared/CopyButton'
import {
    formatIndiaTime,
    formatInZone,
    INDIA_ZONE,
} from '../../helpers/sendSchedule'
import {
    FONT,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
} from '../../theme/tokens'
import Overline from './Overline'

const recipients = (n) => `${n} recipient${n === 1 ? '' : 's'}`

/** The line under the title: what happened, or why it failed. */
const outcomeText = (result) => {
    if (!result.success)
        return (
            result.reason ||
            'Something went wrong. Check the event type and try again.'
        )
    if (result.scheduledFor)
        return `${recipients(result.total)} will get it on ${
            result.zone && result.zone !== INDIA_ZONE
                ? `${formatInZone(result.scheduledFor, result.zone)} (${
                      result.zone
                  }), ${formatIndiaTime(result.scheduledFor)}`
                : formatIndiaTime(result.scheduledFor)
        }.`
    return `${recipients(result.total)} queued.`
}

/**
 * The card after Send: the outcome and the send's Report ID, which Reports
 * searches by, with a copy button and a "View report" link. The id is shown
 * on a failure too: a send that failed after its bulk log was written is
 * still found by it.
 *
 * @param {object} props
 * @param {{success: boolean, total?: number, reason?: string,
 *     scheduledFor?: number, zone?: string}} props.result
 */
const SendResult = ({
    result,
    reportId,
    onViewReport,
    onSendAnother,
    onBack,
}) => {
    const tone = result.success ? STATUS.success : STATUS.error
    const Icon = result.success ? CheckCircleIcon : ErrorOutlineIcon
    let title = 'Send Failed'
    if (result.success)
        title = result.scheduledFor
            ? 'Bulk Send Scheduled'
            : 'Bulk Send Triggered'
    return (
        <Paper
            elevation={0}
            data-testid="send-result"
            sx={{
                ...cardSx,
                p: 5,
                border: `1px solid ${tone.border}`,
                textAlign: 'center',
            }}
        >
            <Icon sx={{ fontSize: ICON.hero, color: tone.fg, mb: 2 }} />
            <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1, color: TEXT.primary }}
            >
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT.muted }}>
                {outcomeText(result)}
            </Typography>
            {reportId && (
                <Box
                    sx={{
                        mt: 3,
                        mx: 'auto',
                        maxWidth: 560,
                        p: 2,
                        borderRadius: `${RADIUS.md}px`,
                        background: SURFACE.subtle,
                        border: `1px solid ${SURFACE.border}`,
                    }}
                >
                    <Overline component="p">Report ID</Overline>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            variant="body2"
                            data-testid="report-id"
                            sx={{
                                color: TEXT.primary,
                                fontFamily: FONT.mono,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {reportId}
                        </Typography>
                        <CopyButton text={reportId} />
                    </Box>
                    <Typography variant="caption" sx={{ color: TEXT.faint }}>
                        Keep it to find this send in Reports.
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                {reportId && (
                    <Button variant="outlined" onClick={onViewReport}>
                        View report
                    </Button>
                )}
                <Button variant="outlined" onClick={onSendAnother}>
                    Send Another Batch
                </Button>
                <Button
                    variant="text"
                    sx={{
                        color: TEXT.muted,
                        '&:hover': { color: TEXT.primary },
                    }}
                    onClick={onBack}
                >
                    Back to Pipelines
                </Button>
            </Box>
        </Paper>
    )
}

SendResult.propTypes = {
    result: PropTypes.shape({
        success: PropTypes.bool.isRequired,
        total: PropTypes.number,
        reason: PropTypes.string,
        scheduledFor: PropTypes.number,
        zone: PropTypes.string,
    }).isRequired,
    reportId: PropTypes.string,
    onViewReport: PropTypes.func.isRequired,
    onSendAnother: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
}

export default SendResult
